// src/pages/shop/ShopWaiter.jsx
//
// Хаб официанта: список открытых заказов (свои — у сотрудника, все — у владельца/админа)
// и кнопка «Взять заказ». Тап по заказу → карточка. Ссылка на историю закрытых.

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cafeOrders as cafeApi, shop as shopApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import { useMyCurrency, useMyShop, setMyShop } from '../../hooks/useMyShop';
import { useAuth } from '../../hooks/useAuth';
import { formatMoney } from '../../data/currencies';
import { errorMessage } from '../../utils/errorMessage';
import Icon from '../../components/Icon';
import BottomNav from '../../components/BottomNav';
import { useShopNav } from './ShopHome';

function orderProgress(order) {
  const its = order.items || [];
  if (!its.length) return 'accepted';
  if (its.every(i => i.cookStatus === 'done')) return 'ready';
  if (its.some(i => i.cookStatus === 'cooking')) return 'cooking';
  return 'accepted';
}

export default function ShopWaiter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();
  const currency = useMyCurrency();
  const { isOwner, can } = useAuth();
  const navItems = useShopNav();
  const myShop = useMyShop();

  const DEFAULT_TABLES = 12;
  const savedTables = Number(myShop?.moduleConfig?.tableCount);
  const [tablesOpen, setTablesOpen] = useState(false);
  const [tableCount, setTableCount] = useState(
    Number.isFinite(savedTables) && savedTables >= 0 ? savedTables : DEFAULT_TABLES
  );
  useEffect(() => {
    const n = Number(myShop?.moduleConfig?.tableCount);
    if (Number.isFinite(n) && n >= 0) setTableCount(n);
  }, [myShop]);

  const saveTableCount = async (n) => {
    const v = Math.max(0, Math.min(99, n));
    setTableCount(v); // мгновенный отклик
    try {
      const updated = await shopApi.updateModules({ tableCount: v });
      // Обновляем кэш магазина, иначе экран заказа прочитает старое значение,
      // а счётчик сбросится при возврате на этот экран.
      if (updated && updated.id) setMyShop(updated);
      // Подстраховка: если бэкенд-патч ещё не накатан и tableCount не вернулся —
      // сообщаем явно, а не молчим (иначе «не запоминается» без причины).
      if (updated && (!updated.moduleConfig || updated.moduleConfig.tableCount == null)) {
        show(t('waiter.tablesNotSaved', 'Число столов не сохранилось. Обновите серверную часть.'), 'error');
      }
    } catch (e) {
      show(errorMessage(e, t), 'error');
    }
  };

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const privileged = isOwner || can('all');

  const load = async () => {
    try { const r = await cafeApi.list('open'); setOrders(r.items || []); }
    catch (e) { show(errorMessage(e, t), 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const progressPill = (p) => {
    const map = {
      ready:    { label: t('waiter.stReady'),    color: 'var(--success)',  bg: 'var(--success-bg, rgba(46,160,67,.12))' },
      cooking:  { label: t('waiter.stCooking'),  color: 'var(--warning, #b8860b)', bg: 'rgba(184,134,11,.12)' },
      accepted: { label: t('waiter.stAccepted'), color: 'var(--fg-muted)', bg: 'var(--bg-subtle)' },
    };
    const s = map[p] || map.accepted;
    return (
      <span style={{ fontSize: 11, fontWeight: 700, color: s.color, background: s.bg, padding: '2px 8px', borderRadius: 999, flexShrink: 0, whiteSpace: 'nowrap' }}>
        {s.label}
      </span>
    );
  };

  return (
    <div className="page" style={{ paddingBottom: 90 }}>
      {ToastView}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 className="page-title">{t('modules.waiter')}</h1>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/waiter/history')} style={{ width: 'auto', flexShrink: 0 }}>{t('waiter.history')}</button>
      </div>

      <div style={{ padding: '0 20px' }}>
        <button className="btn btn-primary" onClick={() => navigate('/waiter/new')} style={{ width: '100%', marginBottom: isOwner ? 8 : 16 }}>
          <Icon name="plus" size={18} /> {t('waiter.newOrder')}
        </button>

        {/* Настройка числа столов — только владельцу */}
        {isOwner && (
          <div style={{ marginBottom: 16 }}>
            <button
              className="btn btn-ghost"
              onClick={() => setTablesOpen(v => !v)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <Icon name="settings" size={16} />
              {t('waiter.setupTables', 'Настроить столы')}
              <span style={{ display: 'inline-flex', transform: tablesOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
                <Icon name="chevronDown" size={15} />
              </span>
            </button>

            {tablesOpen && (
              <div className="card" style={{ marginTop: 8, padding: '14px 16px', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{t('waiter.tablesCount', 'Столов в зале')}</div>
                    <div style={{ fontSize: 12, color: 'var(--fg-faint)' }}>{t('waiter.tablesHint', 'Официант выберет из этой плитки')}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button type="button" onClick={() => saveTableCount(tableCount - 1)} style={stepBtn} aria-label="−">
                      <Icon name="minus" size={16} stroke={2.5} />
                    </button>
                    <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 700, fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>{tableCount}</span>
                    <button type="button" onClick={() => saveTableCount(tableCount + 1)} style={stepBtn} aria-label="+">
                      <Icon name="plus" size={16} stroke={2.5} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
        ) : orders.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 13 }}>{t('waiter.noOpen')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {orders.map(o => (
              <button
                key={o.id} onClick={() => navigate(`/waiter/order/${o.id}`)}
                className="card" style={{ padding: '12px 14px', textAlign: 'left', width: '100%', border: '1px solid var(--border)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 800 }}>{t('waiter.tableShort')} {o.tableLabel}</span>
                    <span style={{ fontSize: 12, color: 'var(--fg-faint)' }}>№{o.number}</span>
                  </div>
                  {progressPill(orderProgress(o))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                    {t('waiter.itemsCount', { count: (o.items || []).length })}
                    {privileged && o.createdByName ? ` · ${o.createdByName}` : ''}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatMoney(o.total, currency)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <BottomNav items={navItems} />
    </div>
  );
}

const stepBtn = {
  width: 32, height: 32, borderRadius: 'var(--r-sm)',
  border: '1.5px solid var(--border)', background: 'var(--paper, #fff)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: 'var(--fg)', cursor: 'pointer', flexShrink: 0,
};
